import brandit_rails from '../api/brandit_rails'
import { setAuthTokenAction } from '../redux/storeConfig/slices/slice.auth'
import * as Types from './types'

/** @typedef {import('redux').Dispatch} Dispatch */
/** @typedef {import('redux').Action} Action */

/**
 * Logs out the current user by dispatching a Redux action.
 *
 * @returns {function(Dispatch): Promise<{ organization: import('../reducers/currentReducer').CurrentOrganization, token: string } | null>} The organization data if successful, or null if the request fails.
 */
export const agencyViewLogout = () => async dispatch => {
  /**
   * @typedef {Object} AgencyLogoutResponse
   * @property {{ organization: import('../reducers/currentReducer').CurrentOrganization, token: string }} data - The organization data returned by the API.
   * @property {number} status - The HTTP status code of the response.
   */

  /** @type {AgencyLogoutResponse} */
  const { data, status } = await brandit_rails.get('/admin/organization/logout_agency')

  if (status !== 201) return null

  dispatch({ type: Types.CURRENT_ACTIONS.SET_ORGANIZATION, payload: data.organization })
  dispatch(setAuthTokenAction(data.token))

  return data
}

/**
 * Logs out the current user by dispatching a Redux action.
 *
 * @param {number} agencyId - The ID of the agency to log in as.
 * @param {boolean} session - Whether to create a session or not.
 *
 * @returns {function{Dispatch}: Promise<{ agency: import('../reducers/currentReducer').CurrentAgency, token: string } | null>} The agency data if successful, or null if the request fails.
 */
export const agencyLogin =
  (agencyId, session = true) =>
  async dispatch => {
    /**
     * @typedef {Object} AgencyLoginResponse
     * @property {{ agency: import('../reducers/currentReducer').CurrentAgency, token: string }} data - The agency data and token returned by the API.
     * @property {number} status - The HTTP status code of the response.
     */

    /** @type {AgencyLoginResponse} */
    const { data, status } = await brandit_rails.get(`/admin/organization/login_as_agency/${agencyId}`)

    if (status !== 201) return null

    if (session) dispatch(setAuthTokenAction(data.token))
    dispatch({ type: Types.CURRENT_ACTIONS.SET_AGENCY, payload: data.agency })

    return data
  }

/**
 * Logs out the current user by dispatching a Redux action.
 *
 * @param {number} organizationId - The ID of the organization to log in as
 * @param {boolean} session - Whether to create a session or not.
 *
 * @returns {function(Dispatch): Promise<{ organization: import('../reducers/currentReducer').CurrentOrganization, token: string } | null>} The organization data if successful, or null if the request fails.
 */
export const loginAsOrganization =
  (organizationId, session = true) =>
  async dispatch => {
    /**
     * @typedef {Object} OrganizationLoginResponse
     * @property {{ organization: import('../reducers/currentReducer').CurrentOrganization, token: string }} data - The agency data and token returned by the API.
     * @property {number} status - The HTTP status code of the response.
     */

    /** @type {OrganizationLoginResponse} */
    const { data, status } = await brandit_rails.get(`/admin/organization/login_as_organization/${organizationId}`)

    if (status !== 201) return null

    dispatch({ type: Types.CURRENT_ACTIONS.SET_ORGANIZATION, payload: data.organization })
    if (session) {
      dispatch(setAuthTokenAction(data.token))
    }

    return data
  }

/**
 * Sets the current organization by dispatching a Redux action.
 *
 * @param {import('../reducers/currentReducer').CurrentOrganization} organization
 * @returns {function(Dispatch): void}
 */
export const setCurrentOrganization = organization => async dispatch => {
  dispatch({ type: Types.CURRENT_ACTIONS.SET_ORGANIZATION, payload: organization })
}
